import type { Currency, TimeFrequency } from '@epilot/pricing'
import {
  DEFAULT_CURRENCY,
  normalizeValueToFrequencyUnit,
  toDinero,
  getTaxValue
} from '@epilot/pricing'
import type { CompositePriceItem, PriceItem } from '@epilot/pricing-client'

/**
 * Extracts the unit from a price item.
 * RULE: Gets the first unit from the first price component item.
 */
const getUnitFromPriceItem = (
  item: PriceItem | CompositePriceItem
): string | undefined => {
  return item?.is_composite_price && Array.isArray(item?.item_components)
    ? item?.item_components.find(
        (component) =>
          component._price?.variable_price && component._price?.unit
      )?._price?.unit
    : item?._price?.unit
}

/**
 * Extracts the tax rate from a price item.
 * RULE: Gets the first defined tax rate from the first price component item.
 */
const getTaxValueFromPriceItem = (
  item: PriceItem | CompositePriceItem
): number | undefined => {
  const taxRate =
    item?.is_composite_price && Array.isArray(item?.item_components)
      ? item?.item_components.find(
          (component) =>
            Array.isArray(component._price?.tax) && component._price?.tax[0]
        )?._price?.tax?.[0]
      : item?._price?.tax?.[0]

  return getTaxValue(taxRate)
}

/**
 * Converts a decimal amount to a yearly amount
 */
const getYearlyDecimalAmount = (
  decimalAmount: string | undefined,
  billingPeriod: TimeFrequency
) => {
  return normalizeValueToFrequencyUnit(
    decimalAmount || '0',
    billingPeriod as TimeFrequency,
    'yearly',
    12
  ).toString()
}

/**
 * Gets the base amounts for the consumption
 */
const getConsumptionBasedAmounts = (
  unitAmountDecimal: string | undefined,
  consumption = 0,
  billingPeriod: TimeFrequency,
  currency: Currency = DEFAULT_CURRENCY
): {
  yearlyAmountDecimal: string | undefined
  amountDecimal: string | undefined
} => {
  if (!unitAmountDecimal || !consumption) {
    return {
      yearlyAmountDecimal: undefined,
      amountDecimal: undefined
    }
  }

  const dineroObjectFromUnitAmountDecimal = toDinero(
    unitAmountDecimal,
    currency
  )

  const yearlyTotal = dineroObjectFromUnitAmountDecimal
    .multiply(consumption)
    .toUnit()
    .toString()

  const normalizedBillingPeriod = normalizeValueToFrequencyUnit(
    yearlyTotal,
    'yearly',
    billingPeriod as TimeFrequency,
    12
  ).toString()

  return {
    yearlyAmountDecimal: yearlyTotal,
    amountDecimal: normalizedBillingPeriod
  }
}

export {
  getYearlyDecimalAmount,
  getConsumptionBasedAmounts,
  getUnitFromPriceItem,
  getTaxValueFromPriceItem
}
